/**
 * PATH: apps/quote/src/lib/quote-builder-math/formula.ts
 * FILE_ID: QB-MATH-FORMULA-001
 * PURPOSE: Pure Excel-style "=" formula parser + evaluator for line-item cells.
 * WHY: Estimators want to type =qty * 0.5 or =SUM(labour, material) into a cell
 *      and have it evaluate live + persist. Mirrors the subset of Excel that
 *      shows up in real Bravada quote sheets.
 * FLOW:
 *   ┌──────────────┐   ┌──────────┐   ┌────────────┐   ┌─────────────┐
 *   │ source text  │ → │ tokenize │ → │   parse    │ → │  evaluate   │
 *   └──────────────┘   └──────────┘   └────────────┘   └─────────────┘
 *                                                            │
 *                                                            ▼
 *                                                  { ok, value | error }
 * DEPENDENCIES:
 *   - none (pure module, no React, no DOM)
 * RELATED:
 *   - QB-MATH-COMPUTELINE-001 (computeLine.ts) — derived numeric defaults
 *   - apps/quote/src/components/builder/scope-sheet-grid/EditableCell.tsx
 *
 * SUPPORTED SYNTAX (deliberately small — Excel-compatible subset):
 *   - Numeric literals:      12, 12.5, .5, 1e3
 *   - Identifiers (vars):    qty, pmpd, rate, labour_chargeout, ...
 *   - Operators:             +  -  *  /  ^   (^ = power)
 *   - Unary minus:           -qty,  -(qty + 1)
 *   - Parentheses:           (qty + 1) * rate
 *   - Functions:             SUM, AVG, MIN, MAX, ABS, ROUND, CEIL, FLOOR, IF
 *
 * SAFETY:
 *   - No eval(), no Function() — handwritten recursive-descent parser.
 *   - Division by zero → #DIV/0! error (not Infinity / NaN leak).
 *   - Unknown identifier → #NAME? error.
 *   - Wrong arg count → #N/A error.
 *   - Numeric overflow / NaN → #NUM! error.
 *
 * The leading "=" is stripped by the caller (via isFormulaText). Internally
 * this engine accepts a bare expression so it can be reused for nested calls.
 */

// =============================================================================
// PUBLIC TYPES
// =============================================================================

export type FormulaContext = Readonly<Record<string, number | null | undefined>>

export type FormulaError =
  | '#DIV/0!'
  | '#NAME?'
  | '#VALUE!'
  | '#NUM!'
  | '#N/A'
  | '#SYNTAX!'

export type FormulaResult =
  | { ok: true; value: number }
  | { ok: false; error: FormulaError; message: string }

// =============================================================================
// TOP-LEVEL HELPERS
// =============================================================================

/**
 * Returns true iff `raw` is a formula expression (starts with "=", trimmed).
 * Matches Excel: any cell value beginning with "=" is a formula.
 */
export function isFormulaText(raw: unknown): raw is string {
  if (typeof raw !== 'string') return false
  return raw.trim().startsWith('=')
}

/**
 * Strips the leading "=" (and trims whitespace). Returns the bare expression.
 * Caller is responsible for first verifying with isFormulaText.
 */
export function stripFormulaPrefix(raw: string): string {
  const trimmed = raw.trim()
  return trimmed.startsWith('=') ? trimmed.slice(1).trim() : trimmed
}

/**
 * Evaluate a raw cell value. If it's a formula ("=..."), parses + evaluates
 * against the context. Otherwise returns the parsed numeric value of the raw.
 *
 * This is the function called from React render paths — it is a hot path,
 * keep it allocation-light (no error stacks, plain object results).
 */
export function evaluateCell(
  raw: unknown,
  ctx: FormulaContext,
): FormulaResult {
  if (raw === null || raw === undefined || raw === '') {
    return { ok: true, value: 0 }
  }
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      return { ok: false, error: '#NUM!', message: 'non-finite number' }
    }
    return { ok: true, value: raw }
  }
  if (typeof raw !== 'string') {
    return { ok: false, error: '#VALUE!', message: 'unsupported value type' }
  }
  if (isFormulaText(raw)) {
    return evaluateFormula(stripFormulaPrefix(raw), ctx)
  }
  // Plain literal — let JS parse it.
  const n = Number(raw.trim())
  if (Number.isFinite(n)) return { ok: true, value: n }
  return { ok: false, error: '#VALUE!', message: `not a number: ${raw}` }
}

/**
 * Evaluate a bare expression string (no leading "=").
 */
export function evaluateFormula(
  expr: string,
  ctx: FormulaContext,
): FormulaResult {
  try {
    const tokens = tokenize(expr)
    const parser = new Parser(tokens, expr)
    const ast = parser.parseExpression()
    parser.expectEnd()
    const value = evalAst(ast, ctx)
    if (!Number.isFinite(value)) {
      return { ok: false, error: '#NUM!', message: 'overflow / NaN' }
    }
    return { ok: true, value }
  } catch (e) {
    if (e instanceof FormulaEvalError) {
      return { ok: false, error: e.code, message: e.message }
    }
    return { ok: false, error: '#SYNTAX!', message: (e as Error).message }
  }
}

// =============================================================================
// TOKENIZER
// =============================================================================

type TokenKind =
  | 'NUMBER'
  | 'IDENT'
  | 'PLUS'
  | 'MINUS'
  | 'STAR'
  | 'SLASH'
  | 'CARET'
  | 'LPAREN'
  | 'RPAREN'
  | 'COMMA'
  | 'EOF'

interface Token {
  kind: TokenKind
  text: string
  pos: number
}

function tokenize(src: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  while (i < src.length) {
    const c = src[i]
    // whitespace
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++
      continue
    }
    // single-char operators
    const single: Record<string, TokenKind> = {
      '+': 'PLUS',
      '-': 'MINUS',
      '*': 'STAR',
      '/': 'SLASH',
      '^': 'CARET',
      '(': 'LPAREN',
      ')': 'RPAREN',
      ',': 'COMMA',
    }
    if (single[c]) {
      tokens.push({ kind: single[c], text: c, pos: i })
      i++
      continue
    }
    // number: digits | digits.digits | .digits | with optional e[+-]?digits
    if ((c >= '0' && c <= '9') || c === '.') {
      const start = i
      let sawDigit = false
      while (i < src.length && src[i] >= '0' && src[i] <= '9') {
        i++
        sawDigit = true
      }
      if (src[i] === '.') {
        i++
        while (i < src.length && src[i] >= '0' && src[i] <= '9') {
          i++
          sawDigit = true
        }
      }
      if (src[i] === 'e' || src[i] === 'E') {
        i++
        if (src[i] === '+' || src[i] === '-') i++
        while (i < src.length && src[i] >= '0' && src[i] <= '9') i++
      }
      if (!sawDigit) {
        throw new SyntaxError(`bad number at ${start}: '${src.slice(start, i)}'`)
      }
      tokens.push({ kind: 'NUMBER', text: src.slice(start, i), pos: start })
      continue
    }
    // identifier: [a-zA-Z_][a-zA-Z0-9_]*
    if ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_') {
      const start = i
      while (
        i < src.length &&
        ((src[i] >= 'a' && src[i] <= 'z') ||
          (src[i] >= 'A' && src[i] <= 'Z') ||
          (src[i] >= '0' && src[i] <= '9') ||
          src[i] === '_')
      ) {
        i++
      }
      tokens.push({ kind: 'IDENT', text: src.slice(start, i), pos: start })
      continue
    }
    throw new SyntaxError(`unexpected char '${c}' at ${i}`)
  }
  tokens.push({ kind: 'EOF', text: '', pos: src.length })
  return tokens
}

// =============================================================================
// PARSER  (recursive descent, classic precedence-climbing)
// =============================================================================
// Grammar:
//   expression  := term (('+' | '-') term)*
//   term        := factor (('*' | '/') factor)*
//   factor      := unary ('^' factor)?      // right-assoc
//   unary       := ('+' | '-')* primary
//   primary     := NUMBER | call | IDENT | '(' expression ')'
//   call        := IDENT '(' arglist? ')'
//   arglist     := expression (',' expression)*

type Ast =
  | { type: 'num'; value: number }
  | { type: 'var'; name: string }
  | { type: 'unary'; op: '+' | '-'; expr: Ast }
  | { type: 'bin'; op: '+' | '-' | '*' | '/' | '^'; left: Ast; right: Ast }
  | { type: 'call'; name: string; args: Ast[] }

class Parser {
  private pos = 0

  constructor(
    private tokens: Token[],
    private src: string,
  ) {}

  parseExpression(): Ast {
    let left = this.parseTerm()
    while (this.peek('PLUS') || this.peek('MINUS')) {
      const op = this.advance().kind === 'PLUS' ? '+' : '-'
      const right = this.parseTerm()
      left = { type: 'bin', op, left, right }
    }
    return left
  }

  private parseTerm(): Ast {
    let left = this.parseFactor()
    while (this.peek('STAR') || this.peek('SLASH')) {
      const op = this.advance().kind === 'STAR' ? '*' : '/'
      const right = this.parseFactor()
      left = { type: 'bin', op, left, right }
    }
    return left
  }

  private parseFactor(): Ast {
    const left = this.parseUnary()
    if (this.peek('CARET')) {
      this.advance()
      const right = this.parseFactor() // right-associative
      return { type: 'bin', op: '^', left, right }
    }
    return left
  }

  private parseUnary(): Ast {
    if (this.peek('PLUS')) {
      this.advance()
      return { type: 'unary', op: '+', expr: this.parseUnary() }
    }
    if (this.peek('MINUS')) {
      this.advance()
      return { type: 'unary', op: '-', expr: this.parseUnary() }
    }
    return this.parsePrimary()
  }

  private parsePrimary(): Ast {
    const tok = this.advance()
    if (tok.kind === 'NUMBER') {
      const n = Number(tok.text)
      if (!Number.isFinite(n)) {
        throw new SyntaxError(`bad number literal '${tok.text}'`)
      }
      return { type: 'num', value: n }
    }
    if (tok.kind === 'IDENT') {
      if (this.peek('LPAREN')) {
        this.advance()
        const args: Ast[] = []
        if (!this.peek('RPAREN')) {
          args.push(this.parseExpression())
          while (this.peek('COMMA')) {
            this.advance()
            args.push(this.parseExpression())
          }
        }
        this.expect('RPAREN', ')')
        return { type: 'call', name: tok.text, args }
      }
      return { type: 'var', name: tok.text }
    }
    if (tok.kind === 'LPAREN') {
      const inner = this.parseExpression()
      this.expect('RPAREN', ')')
      return inner
    }
    throw new SyntaxError(
      `unexpected '${tok.text || tok.kind}' at ${tok.pos} in '${this.src}'`,
    )
  }

  expectEnd(): void {
    const tok = this.peekTok()
    if (tok.kind !== 'EOF') {
      throw new SyntaxError(
        `unexpected '${tok.text || tok.kind}' at ${tok.pos} (expected end)`,
      )
    }
  }

  private peek(kind: TokenKind): boolean {
    return this.tokens[this.pos].kind === kind
  }

  private peekTok(): Token {
    return this.tokens[this.pos]
  }

  private advance(): Token {
    return this.tokens[this.pos++]
  }

  private expect(kind: TokenKind, label: string): Token {
    const tok = this.advance()
    if (tok.kind !== kind) {
      throw new SyntaxError(
        `expected '${label}' but got '${tok.text || tok.kind}' at ${tok.pos}`,
      )
    }
    return tok
  }
}

// =============================================================================
// EVALUATOR
// =============================================================================

class FormulaEvalError extends Error {
  code: FormulaError
  constructor(code: FormulaError, message: string) {
    super(message)
    this.code = code
  }
}

type FnSpec = {
  /** -1 = variadic (>= 1 arg). */
  arity: number
  fn: (args: number[]) => number
}

const FUNCTIONS: Record<string, FnSpec> = {
  SUM: { arity: -1, fn: (a) => a.reduce((s, x) => s + x, 0) },
  AVG: { arity: -1, fn: (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0) },
  AVERAGE: { arity: -1, fn: (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0) },
  MIN: { arity: -1, fn: (a) => Math.min(...a) },
  MAX: { arity: -1, fn: (a) => Math.max(...a) },
  ABS: { arity: 1, fn: (a) => Math.abs(a[0]) },
  ROUND: {
    arity: -1, // ROUND(x) or ROUND(x, ndigits)
    fn: (a) => {
      if (a.length < 1) {
        throw new FormulaEvalError('#N/A', 'ROUND requires 1 or 2 args')
      }
      const x = a[0]
      const d = a.length >= 2 ? Math.trunc(a[1]) : 0
      const m = Math.pow(10, d)
      return Math.round(x * m) / m
    },
  },
  CEIL: { arity: 1, fn: (a) => Math.ceil(a[0]) },
  CEILING: { arity: 1, fn: (a) => Math.ceil(a[0]) },
  FLOOR: { arity: 1, fn: (a) => Math.floor(a[0]) },
  // NOTE: IF is short-circuited by the evaluator (lazy branch eval), so the
  // function body here is only used as a fallback when both branches were
  // already evaluated successfully (Excel parity = lazy by spec but this keeps
  // the registry uniform).
  IF: {
    arity: 3,
    fn: (a) => (a[0] !== 0 ? a[1] : a[2]),
  },
}

function evalAst(node: Ast, ctx: FormulaContext): number {
  switch (node.type) {
    case 'num':
      return node.value
    case 'var': {
      const key = node.name
      if (!(key in ctx)) {
        // Try lower-case lookup (estimators often type QTY when ctx has qty).
        const lc = key.toLowerCase()
        if (lc in ctx) {
          return toNumber(ctx[lc], key)
        }
        throw new FormulaEvalError('#NAME?', `unknown name '${key}'`)
      }
      return toNumber(ctx[key], key)
    }
    case 'unary': {
      const v = evalAst(node.expr, ctx)
      return node.op === '-' ? -v : v
    }
    case 'bin': {
      const l = evalAst(node.left, ctx)
      const r = evalAst(node.right, ctx)
      switch (node.op) {
        case '+':
          return l + r
        case '-':
          return l - r
        case '*':
          return l * r
        case '/':
          if (r === 0) {
            throw new FormulaEvalError('#DIV/0!', 'division by zero')
          }
          return l / r
        case '^':
          return Math.pow(l, r)
      }
      // unreachable
      return 0
    }
    case 'call': {
      const fname = node.name.toUpperCase()
      const spec = FUNCTIONS[fname]
      if (!spec) {
        throw new FormulaEvalError('#NAME?', `unknown function '${node.name}'`)
      }
      if (spec.arity !== -1 && node.args.length !== spec.arity) {
        throw new FormulaEvalError(
          '#N/A',
          `${fname} expects ${spec.arity} arg(s), got ${node.args.length}`,
        )
      }
      if (spec.arity === -1 && node.args.length === 0) {
        throw new FormulaEvalError('#N/A', `${fname} requires at least 1 arg`)
      }
      // Short-circuit IF — Excel parity. Only the chosen branch is evaluated,
      // so e.g. =IF(qty, labour/qty, 0) does NOT trip #DIV/0! when qty=0.
      if (fname === 'IF') {
        const cond = evalAst(node.args[0], ctx)
        const result = cond !== 0 ? evalAst(node.args[1], ctx) : evalAst(node.args[2], ctx)
        if (!Number.isFinite(result)) {
          throw new FormulaEvalError('#NUM!', 'IF produced non-finite result')
        }
        return result
      }
      const args = node.args.map((a) => evalAst(a, ctx))
      const result = spec.fn(args)
      if (!Number.isFinite(result)) {
        throw new FormulaEvalError('#NUM!', `${fname} produced non-finite result`)
      }
      return result
    }
  }
}

function toNumber(v: unknown, name: string): number {
  if (v === null || v === undefined || v === '') return 0
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) {
      throw new FormulaEvalError('#NUM!', `'${name}' is non-finite`)
    }
    return v
  }
  if (typeof v === 'string') {
    const trimmed = v.trim()
    if (trimmed === '') return 0
    const n = Number(trimmed)
    if (Number.isFinite(n)) return n
    throw new FormulaEvalError('#VALUE!', `'${name}' is not a number: '${v}'`)
  }
  throw new FormulaEvalError('#VALUE!', `'${name}' has unsupported type`)
}
